import { MenuTreeNode } from "src/api/client-api/menu/useNavigationMenu";
import { whereAmI } from "src/services/urlSwitcher";

type AppPosition = ReturnType<typeof whereAmI>;

const hasKnownSection = (position: AppPosition): boolean => {
    return (
        position.inCompany ||
        position.inProductValueChains ||
        position.inProduct ||
        position.inIntermediateProduct ||
        position.inIngredients ||
        position.inSuppliers ||
        position.inPackagings ||
        position.inDistribution ||
        position.inTransport ||
        position.inTransformations ||
        position.inNetwork
    );
};

const isSameSection = (appPosition: AppPosition, menuItemPosition: AppPosition): boolean => {
    if (appPosition.inProductValueChains || menuItemPosition.inProductValueChains) {
        return appPosition.inProductValueChains && menuItemPosition.inProductValueChains;
    }

    if (appPosition.inCompany && menuItemPosition.inCompany) {
        return true;
    }

    if (appPosition.inProduct && menuItemPosition.inProduct) {
        return true;
    }

    if (appPosition.inIntermediateProduct && menuItemPosition.inIntermediateProduct) {
        return true;
    }

    if (appPosition.inIngredients && menuItemPosition.inIngredients) {
        return true;
    }

    if (appPosition.inSuppliers && menuItemPosition.inSuppliers) {
        return true;
    }

    if (appPosition.inPackagings && menuItemPosition.inPackagings) {
        return true;
    }

    if (appPosition.inDistribution && menuItemPosition.inDistribution) {
        return true;
    }

    if (appPosition.inTransport && menuItemPosition.inTransport) {
        return true;
    }

    if (appPosition.inTransformations && menuItemPosition.inTransformations) {
        return true;
    }

    if (appPosition.inNetwork && menuItemPosition.inNetwork) {
        return true;
    }

    return false;
};

/**
 * Check if a menu URL matches the current path
 */
export const isUrlMatch = (menuUrl: string, currentPath: string): boolean => {
    if (!menuUrl) return false;

    // Clean URLs (remove query params and hash)
    const cleanMenuUrl = menuUrl.split("?")[0].split("#")[0];
    const cleanCurrentPath = currentPath.split("?")[0].split("#")[0];

    // Section-based matching is centralized in whereAmI.
    // It keeps the inCompany exclusion rule and adds dedicated value-chains behavior.
    const appPosition = whereAmI({ url: cleanCurrentPath });
    const menuItemPosition = whereAmI({ url: cleanMenuUrl });
    if (isSameSection(appPosition, menuItemPosition)) {
        return true;
    }

    // Exact match
    if (cleanMenuUrl === cleanCurrentPath) {
        return true;
    }

    // If one of the paths is in a known section but they are not the same section,
    // we should not rely on raw prefix matching.
    if (hasKnownSection(appPosition) || hasKnownSection(menuItemPosition)) {
        return false;
    }

    // Prefix match fallback for routes outside known sections.
    if (cleanCurrentPath.startsWith(cleanMenuUrl + "/")) {
        return true;
    }

    return false;
};

/**
 * Find all active menu URLs for the current path
 * Returns URLs sorted by specificity (longest/most specific first)
 */
export const findActiveMenuUrls = (tree: MenuTreeNode[], currentPath: string): string[] => {
    const activeUrls: string[] = [];

    const traverse = (nodes: MenuTreeNode[]) => {
        for (const node of nodes) {
            if (node.item.url && isUrlMatch(node.item.url, currentPath)) {
                activeUrls.push(node.item.url);
            }
            traverse(node.children);
        }
    };

    traverse(tree);

    // Sort by URL length (descending) to prioritize more specific matches
    // e.g., /company/26/intermediate-products should match before /company/26/products
    return activeUrls.sort((a, b) => b.length - a.length);
};

/**
 * Check if a menu item should be highlighted as active
 */
export const isMenuItemActive = (menuUrl: string | undefined, currentPath: string): boolean => {
    if (!menuUrl) return false;
    return isUrlMatch(menuUrl, currentPath);
};
